import type { AssistantDraft } from '@fin/shared';

const WALLET_PROBLEMS = ['wallet_not_understood', 'unknown_wallet', 'ambiguous_wallet', 'archived_wallet'];

/** form fields a parsed phrase may set */
export interface DraftChanges {
  mode?: 'expense' | 'income' | 'transfer' | 'debt';
  debtKind?: 'debt' | 'loan' | 'repayment' | 'debt_collection';
  amount?: number;
  /** null: a wallet was said but not understood — the form must not keep or guess one */
  walletId?: number | null;
  toWalletId?: number;
  categoryId?: number;
  date?: string;
  note?: string;
  fee?: number;
  amountTo?: number;
  excludeFromReport?: boolean;
  contactId?: number;
  debtId?: number;
  lineId?: number;
}

/**
 * What a new parse of the whole phrase changes in the form. Only fields whose recognized value differs
 * from the previous parse move, so «трата 5000 на продукты… 5500» changes the amount alone and a field
 * picked by hand stays until it is said again. An unsaid wallet or date never overrides the form's own
 * defaults (the primary wallet, today). `heard` is the memory to pass into the next call; pass `{}` to
 * apply every field the phrase names («Распознать заново»).
 */
export function draftChanges(heard: Partial<AssistantDraft>, d: AssistantDraft): { changes: DraftChanges; heard: Partial<AssistantDraft> } {
  const changes: DraftChanges = {};
  if (!d.mode) return { changes, heard };
  const changed = (k: keyof AssistantDraft) => d[k] !== null && d[k] !== heard[k];
  if (changed('mode')) changes.mode = d.mode;
  if (changed('debtKind')) changes.debtKind = d.debtKind!;
  if (changed('amount')) changes.amount = d.amount!;
  const unclear = d.problems.some((p) => WALLET_PROBLEMS.includes(p));
  // cleared once when the problem appears; a wallet picked by hand afterwards stays
  if (unclear && !heard.problems?.some((p) => WALLET_PROBLEMS.includes(p))) changes.walletId = null;
  else if (!unclear && d.walletSaid && changed('walletId')) changes.walletId = d.walletId!;
  if (changed('toWalletId')) changes.toWalletId = d.toWalletId!;
  // a new type clears the category in the form, so the recognized one comes back with it
  if (d.categoryId !== null && (changed('categoryId') || changes.mode || changes.debtKind)) changes.categoryId = d.categoryId;
  if (d.dateSaid && changed('date')) changes.date = d.date!;
  if (changed('note')) changes.note = d.note!.charAt(0).toUpperCase() + d.note!.slice(1);
  if (changed('fee')) changes.fee = d.fee!;
  if (changed('amountTo')) changes.amountTo = d.amountTo!;
  if (changed('excludeFromReport')) changes.excludeFromReport = d.excludeFromReport!;
  if (changed('contactId')) changes.contactId = d.contactId!;
  if (changed('debtId')) changes.debtId = d.debtId!;
  if (changed('planLineId')) changes.lineId = d.planLineId!;
  const next = { ...heard };
  for (const [k, v] of Object.entries(d)) if (v !== null) (next as Record<string, unknown>)[k] = v;
  if (unclear) delete next.walletId; // the wallet said next, even the same as before, applies again
  return { changes, heard: next };
}
